import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import api from '../utils/api';
import { Button, Container } from '../components/site/ui';
import { IconCheck } from '../components/icons';
import { PLAN_OPTIONS, planAccent, formatPrice } from '../components/plans';

/**
 * Every branch must return the same shape — `features` included. The cards
 * render once before the pricing fetch resolves, so a branch that omits it
 * leaves callers spreading `undefined`.
 */
function planDisplay(pl) {
  if (pl == null) {
    return { price: 0, includedCases: null, includedUsers: null, isCustom: false, features: [] };
  }
  if (typeof pl === 'number') {
    return { price: pl, includedCases: null, includedUsers: null, isCustom: false, features: [] };
  }
  return {
    price: pl.price ?? null,
    includedCases: pl.includedCases ?? null,
    includedUsers: pl.includedUsers ?? null,
    freeCaseAllowance: pl.freeCaseAllowance ?? null,
    overThresholdFee: pl.overThresholdFee ?? null,
    isCustom: Boolean(pl.isCustom),
    isTrial: Boolean(pl.isTrial),
    features: Array.isArray(pl.features) ? pl.features : []
  };
}

/** Courier plans have different labels/shape from the merchant ladder. */
const COURIER_PLAN_OPTIONS = [
  { value: 'pilot', label: 'Pilot' },
  { value: 'intelligence', label: 'Intelligence' }
];

const AUDIENCES = ['customers', 'merchants', 'couriers'];
const DEFAULT_AUDIENCE = 'merchants';

const PricingPage = () => {
  /**
   * The open tab lives in the URL rather than in component state, so arriving
   * from the courier page lands on courier pricing (and the merchant page on
   * merchant pricing) instead of always opening on the default. It also makes
   * the tab shareable and the back button behave.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('for');
  const audience = AUDIENCES.includes(requested) ? requested : DEFAULT_AUDIENCE;
  // replace, not push: switching tabs shouldn't bury the page you came from
  // under a history entry per click.
  const setAudience = (value) => setSearchParams({ for: value }, { replace: true });
  const [pricing, setPricing] = useState({
    smsPrice: 0.99,
    seatPrice: 67,
    merchantPlanPrices: {}
  });
  const [courierPricing, setCourierPricing] = useState({ courierPlanPrices: {} });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const [merchantRes, courierRes] = await Promise.all([
          api.get('/api/merchants/pricing'),
          api.get('/api/couriers/pricing')
        ]);
        const data = merchantRes.data;
        setPricing((prev) => ({
          smsPrice: data.smsPrice ?? prev.smsPrice,
          seatPrice: data.seatPrice ?? prev.seatPrice,
          merchantPlanPrices: data.merchantPlanPrices || prev.merchantPlanPrices
        }));
        setCourierPricing((prev) => ({
          courierPlanPrices: courierRes.data?.courierPlanPrices || prev.courierPlanPrices
        }));
      } catch (error) {
        console.error('Error fetching pricing:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPricing();
  }, []);

  /**
   * Allowance bullets are derived from the configured numbers so they can
   * never disagree with what is actually enforced; everything after them is
   * the admin-editable selling points for that plan.
   */
  const bulletsFor = (key, pl) => {
    const bullets = [];
    if (pl.includedCases != null) {
      bullets.push(
        `${pl.includedCases.toLocaleString('en-GB')} free case${pl.includedCases === 1 ? '' : 's'}`
      );
    } else {
      bullets.push('Unlimited cases');
    }
    if (pl.includedUsers != null) {
      bullets.push(`${pl.includedUsers} user${pl.includedUsers === 1 ? '' : 's'} included`);
    }
    if (pl.overThresholdFee != null && pl.overThresholdFee > 0) {
      bullets.push(`£${Number(pl.overThresholdFee).toFixed(2)} per extra case`);
    }
    return [...bullets, ...pl.features];
  };

  /** Same idea for the courier ladder, which counts its allowance differently. */
  const courierBulletsFor = (key, pl) => {
    const bullets = [];
    if (key === 'pilot' && pl.freeCaseAllowance != null) {
      bullets.push(`${pl.freeCaseAllowance} free case${pl.freeCaseAllowance === 1 ? '' : 's'}`);
    }
    return [...bullets, ...pl.features];
  };

  const merchantPlans = PLAN_OPTIONS.map((p) => ({
    key: p.value,
    label: p.label,
    ...planDisplay(pricing.merchantPlanPrices?.[p.value])
  })).filter((p) => p.price != null || p.isCustom);

  const courierPlans = COURIER_PLAN_OPTIONS.map((p) => ({
    key: p.value,
    label: p.label,
    ...planDisplay(courierPricing.courierPlanPrices?.[p.value])
  })).filter((p) => p.price != null || p.isCustom);

  const AudienceTab = ({ value, children }) => (
    <button
      type="button"
      onClick={() => setAudience(value)}
      aria-pressed={audience === value}
      className={`rounded-lg px-5 py-2 text-sm font-600 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-brand-900 ${
        audience === value ? 'bg-white text-brand-800 shadow-card' : 'text-white/70 hover:text-white'
      }`}
    >
      {children}
    </button>
  );

  return (
    <>
      {/* Deliberately shallow: the plan cards are the point of this page and
          should be visible without scrolling past a hero first. */}
      <section className="bg-brand-900">
        <Container width="full" className="py-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between lg:gap-8">
            <div className="min-w-0">
              <h1 className="font-display text-[22px] font-800 leading-tight tracking-tight text-white sm:text-[26px]">
                Simple plans for every <span className="text-accent-400">business</span>
              </h1>
              <p className="mt-1 max-w-xl text-sm leading-relaxed text-white/70">
                Free for customers, always. Merchants and couriers pick the plan that fits.
              </p>
            </div>

            <div className="inline-flex shrink-0 gap-1 self-start rounded-xl bg-white/10 p-1 lg:self-auto">
              <AudienceTab value="customers">For customers</AudienceTab>
              <AudienceTab value="merchants">For merchants</AudienceTab>
              <AudienceTab value="couriers">For couriers</AudienceTab>
            </div>
          </div>
        </Container>
      </section>

      <Container width="full" className="py-6 sm:py-8">

      {audience === 'customers' ? (
        <div className="mt-5">
          <div className="mx-auto grid max-w-4xl grid-cols-1 gap-5 sm:grid-cols-2">
            <div className="rounded-2xl bg-brand-50 p-6 ring-1 ring-inset ring-brand-100">
              <p className="font-display text-3xl font-800 tracking-tight text-brand-800">Free</p>
              <p className="mt-2 text-[15px] leading-relaxed text-ink-700">
                Submitting a delivery issue report is always free. No account required.
              </p>
              <Button as={Link} to="/report" variant="accent" className="mt-5">
                Report a delivery issue
              </Button>
            </div>
            <div className="rounded-2xl bg-gold-50 p-6 ring-1 ring-inset ring-gold-100">
              <p className="font-display text-3xl font-800 tracking-tight text-gold-700">
                {loading ? (
                  <span className="inline-block h-8 w-24 animate-pulse rounded bg-gold-100 align-middle" />
                ) : (
                  <>£{Number(pricing.smsPrice).toFixed(2)}</>
                )}
                <span className="ml-1.5 text-sm font-600 text-ink-600">per case</span>
              </p>
              <p className="mt-2 text-[15px] leading-relaxed text-ink-700">
                Optional SMS updates — get a text when your case is reviewed or resolved. Email
                updates are always free.
              </p>
            </div>
          </div>
        </div>
      ) : audience === 'merchants' ? (
        <div className="mt-5">
          <div className="mx-auto grid max-w-2xl grid-cols-1 gap-4 sm:grid-cols-2">
            {merchantPlans.map((plan) => {
              const featured = plan.key === 'merchant';
              const accent = planAccent(plan.key);
              return (
                <div
                  key={plan.key}
                  className={`relative flex flex-col overflow-hidden rounded-2xl border-2 bg-white p-4 transition-shadow ${
                    featured ? `${accent.border} shadow-lifted ring-4 ${accent.ring}` : `${accent.border} hover:shadow-card`
                  }`}
                >
                  {/* The wash is painted first and the badge given a layer of
                      its own: as siblings in the other order the gradient sat
                      on top and bleached the badge text out. */}
                  <div
                    className={`pointer-events-none absolute inset-x-0 top-0 h-20 bg-gradient-to-b ${accent.wash} opacity-70`}
                    aria-hidden="true"
                  />
                  {featured && (
                    <span className="absolute right-4 top-4 z-10 rounded-full bg-gold-500 px-2.5 py-1 text-[10px] font-700 uppercase tracking-[0.08em] text-brand-900">
                      Most popular
                    </span>
                  )}

                  <h3 className="relative font-display text-base font-700 tracking-tight text-ink-900">
                    {plan.label}
                  </h3>

                  <div className="relative mt-2.5 min-h-[3rem]">
                    {loading ? (
                      <span className="inline-block h-7 w-20 animate-pulse rounded bg-ink-100" />
                    ) : (
                      <>
                        <p className="font-display text-2xl font-800 leading-none tracking-tight text-ink-900">
                          {plan.isCustom ? 'Custom' : plan.price === 0 ? 'Free' : `£${formatPrice(plan.price)}`}
                        </p>
                        <p className="mt-1 text-xs text-ink-500">
                          {plan.isCustom
                            ? 'Talk to us'
                            : plan.price === 0
                              ? 'to try it'
                              : // Unlimited-cases plans are seat-priced: the plan
                                // price buys the first user and each additional
                                // one costs the same again.
                                plan.includedCases == null
                                ? 'per user / month'
                                : 'per month'}
                        </p>
                      </>
                    )}
                  </div>

                  <ul className="mt-3 flex-1 space-y-1.5 border-t border-ink-100 pt-3">
                    {bulletsFor(plan.key, plan).map((bullet) => (
                      <li key={bullet} className="flex items-start gap-2 text-[13px] leading-snug text-ink-700">
                        <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full ${accent.badgeBg} ${accent.badgeText}`}>
                          <IconCheck size={11} />
                        </span>
                        {bullet}
                      </li>
                    ))}
                  </ul>

                  {/* Both plans land on the same registration page — it asks
                      for the same business details either way and carries the
                      plan through in ?plan=, which the chooser there
                      pre-selects. Only the paid one goes on to Stripe. */}
                  <Button
                    as={Link}
                    to={`/start-pilot?plan=${plan.key}`}
                    variant={plan.key === 'pilot' ? 'primary' : 'gold'}
                    size="sm"
                    className="mt-4 w-full"
                  >
                    {plan.key === 'pilot' ? 'Start pilot' : 'Get started'}
                  </Button>
                </div>
              );
            })}
          </div>

          {/* Additional seats */}
          <div className="mt-5 flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-gold-50 px-6 py-5 ring-1 ring-inset ring-gold-100">
            <div className="min-w-0">
              <p className="font-display text-[15px] font-700 text-ink-900">
                Additional merchant users
              </p>
              <p className="mt-1 text-sm leading-relaxed text-ink-600">
                Give access to more of your customer service team. Use on any device.
              </p>
            </div>
            <p className="font-display text-xl font-800 tracking-tight text-gold-700">
              £{formatPrice(pricing.seatPrice)}
              <span className="ml-1 text-sm font-600 text-ink-600">per user / month</span>
            </p>
          </div>

          <p className="mt-3 text-center text-xs text-ink-500">
            Prices are subject to change. Current pricing shown.
          </p>
        </div>
      ) : (
        <div className="mt-5">
          <div className="mx-auto grid max-w-2xl grid-cols-1 gap-4 sm:grid-cols-2">
            {courierPlans.map((plan) => {
              const featured = plan.key === 'intelligence';
              // Couriers read blue across the site, the way merchants read gold.
              const accent = featured
                ? {
                    badgeBg: 'bg-info-600',
                    badgeText: 'text-white',
                    ring: 'ring-info-100',
                    border: 'border-info-600',
                    borderSoft: 'border-info-200',
                    wash: 'from-info-50 to-white'
                  }
                : planAccent('pilot');
              return (
                <div
                  key={plan.key}
                  className={`relative flex flex-col overflow-hidden rounded-2xl border-2 bg-white p-4 transition-shadow ${
                    featured ? `${accent.border} shadow-lifted ring-4 ${accent.ring}` : `${accent.border} hover:shadow-card`
                  }`}
                >
                  <div
                    className={`pointer-events-none absolute inset-x-0 top-0 h-20 bg-gradient-to-b ${accent.wash} opacity-70`}
                    aria-hidden="true"
                  />

                  <h3 className="relative font-display text-base font-700 tracking-tight text-ink-900">
                    {plan.label}
                  </h3>

                  <div className="relative mt-2.5 min-h-[3rem]">
                    {loading ? (
                      <span className="inline-block h-7 w-20 animate-pulse rounded bg-ink-100" />
                    ) : (
                      <>
                        <p className="font-display text-2xl font-800 leading-none tracking-tight text-ink-900">
                          {plan.price === 0 ? 'Free' : `£${formatPrice(plan.price)}`}
                        </p>
                        <p className="mt-1 text-xs text-ink-500">
                          {plan.price === 0 ? 'to try it' : 'per month'}
                        </p>
                      </>
                    )}
                  </div>

                  <ul className="mt-3 flex-1 space-y-1.5 border-t border-ink-100 pt-3">
                    {courierBulletsFor(plan.key, plan).map((bullet) => (
                      <li key={bullet} className="flex items-start gap-2 text-[13px] leading-snug text-ink-700">
                        <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full ${accent.badgeBg} ${accent.badgeText}`}>
                          <IconCheck size={11} />
                        </span>
                        {bullet}
                      </li>
                    ))}
                  </ul>

                  {/* Both plans are self-serve now, and both land on the same
                      registration page with that plan pre-selected — exactly
                      like the merchant cards above. */}
                  <Button
                    as={Link}
                    to={`/couriers/start-pilot?plan=${plan.key}`}
                    variant={plan.key === 'intelligence' ? 'info' : 'secondary'}
                    size="sm"
                    className="mt-4 w-full"
                  >
                    {plan.key === 'pilot' ? 'Start free pilot' : 'Get started'}
                  </Button>
                </div>
              );
            })}
          </div>
          <p className="mt-5 text-center text-xs text-ink-500">
            We match reports to your network by carrier name, which our team links to your account
            once you&apos;re signed up.
          </p>
        </div>
      )}
      </Container>
    </>
  );
};

export default PricingPage;
